// File Path : src\hooks\useFabricCanvas.ts

import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Canvas, FabricImage, Rect } from 'fabric';
import { useDispatch, useSelector } from 'react-redux';
import { toggleMenuBar } from 'store/reducers/menu';
import { ConfigurationField, RubberbandMetrics } from 'types/configuration';
import { useParams } from 'react-router-dom';
import { setLabelRubberbandEnabled, setLabelRubberbandDisabled } from 'store/reducers/taggingSlice';
import { RootState } from 'store';
import { RectMetricsForRubberBandType } from 'types/image-viewer';
import { useLocation } from 'react-router';
const cos = (angle: number) => Math.round(Math.cos((angle * Math.PI) / 180));
const sin = (angle: number) => Math.round(Math.sin((angle * Math.PI) / 180));

const zoomLevels = [0.25, 0.5, 0.75, 1, 1.5, 2, 2.5, 3];
const RUBBER_BAND_DIALOG_WIDTH = 250;

interface Props {
  activeField?: ConfigurationField;
  currentPageNumber?: number;
  dualScreenWindowRef?: MutableRefObject<Window | null>;
  secondaryScreenUrl?: string;
  selectedTechniqueFieldId?: number;
  shouldCaptureRectImgContent?: boolean;
  enableRightClick?: boolean;
  expanded?: boolean;
  onRotateResetBack?: () => void;
}

const useFabricCanvas = ({
  activeField,
  currentPageNumber,
  dualScreenWindowRef,
  secondaryScreenUrl = 'document-configuration-secondary-view',
  selectedTechniqueFieldId,
  shouldCaptureRectImgContent,
  enableRightClick,
  expanded = false,
  onRotateResetBack = undefined,
  setActive = undefined
}: Props) => {
  const dispatch = useDispatch();
  const { fileId } = useParams();

  const canvasRef = useRef<HTMLCanvasElement | any>(null);
  const dualScreenIntervalRef = useRef<NodeJS.Timeout>();
  const [fitToScreen, setFitToScreen] = useState<boolean>(false);
  const [enableRubberBand, setEnableRubberBand] = useState<boolean>(false);

  const canvasRefCallback = useCallback((canvasNode: HTMLCanvasElement) => {
    if (canvasNode) {
      canvasRef.current = canvasNode;
    }
  }, []);

  const fCanvasRef = useRef<Canvas>();
  const fImageRef = useRef<FabricImage>();
  const fRectRef = useRef<Rect>();
  const qcRectRef = useRef<Rect | null>(null);
  const offlineHighlightRubberBandData = useRef<RectMetricsForRubberBandType | null>(null);

  const [showRubberbandDialog, setShowRubberbandDialog] = useState(false);
  const [rubberbandMetrics, setRubberbandMetrics] = useState<RubberbandMetrics>();
  const [rubberbandDialogPosition, setRubberbandDialogPosition] = useState<{ left: number; top: number; isEdit?: boolean }>();
  const [dualScreenEnabled, setDualScreenEnabled] = useState(false);
  const [currentZoomLevel, setCurrentZoomLevel] = useState(100);
  const [isMaxZoomIn, setIsMaxZoomIn] = useState(false);
  const [isMaxZoomOut, setIsMaxZoomOut] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  // const [isLabelRubberbandEnabled, setIsLabelRubberbandEnabled] = useState(false);
  const [labelRubberbandMetrics, setLabelRubberbandMetrics] = useState<RubberbandMetrics | null>(null);
  const [showLabelRubberbandDialog, setShowLabelRubberbandDialog] = useState(false);
  const isLabelRubberbandEnabled = useSelector((state: RootState) => state.taggingSlice.isLabelRubberbandEnabled);
  const isAddPatternRubberBandEnabled = useSelector((state: RootState) => state.taggingSlice.isAddPatternRubberBandEnabled);
  const [rectImageContent, setRectImageContent] = useState<string | null>(null);

  const isDrawing = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const isLabelRubberbandEnabledRef = useRef(isLabelRubberbandEnabled);
  const isAddPatternRubberBandEnabledRef = useRef(isAddPatternRubberBandEnabled);
  useEffect(() => {
    isLabelRubberbandEnabledRef.current = isLabelRubberbandEnabled;
  }, [isLabelRubberbandEnabled]); // Sync the ref with the state
  useEffect(() => {
    isAddPatternRubberBandEnabledRef.current = isAddPatternRubberBandEnabled;
  }, [isAddPatternRubberBandEnabled]); // Sync the ref with the state
  useEffect(() => {
    if (fitToScreen && canvasRef?.current) {
      setTimeout(() => {
        onFitToScreenSelect();
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  // To remove rubber band for after added another techinique for same field
  useEffect(() => {
    if (!selectedTechniqueFieldId) {
      const _canvas = fCanvasRef.current;
      const _img = fImageRef.current;
      if (!_canvas || !_img) {
        return;
      }
      // Remove last rubber banded rectangle
      if (fRectRef.current) {
        _canvas.remove(fRectRef.current);
        fRectRef.current = undefined;
      }
    }
  }, [selectedTechniqueFieldId]);

  useEffect(() => {
    const _canvas = fCanvasRef.current;
    const _img = fImageRef.current;
    if (!_canvas || !_img) {
      return;
    }
    // Remove last rubber banded rectangle
    if (fRectRef.current) {
      _canvas.remove(fRectRef.current);
      fRectRef.current = undefined;
    }

    if (activeField && activeField.pageNumber === _img.get('pageNumber') && !imageLoading && fImageRef?.current) {
      const { scaleX, scaleY } = fImageRef.current;
      fRectRef.current = new Rect({
        left: activeField.startX * scaleX,
        top: activeField.startY * scaleY,
        stroke: '#f68d2e',
        strokeWidth: 0,
        fill: 'rgba(246,141,46,0.5)',
        width: activeField.width * scaleX,
        height: activeField.height * scaleY,
        selectable: false
      });
      _canvas.add(fRectRef.current);
    }
  }, [activeField, currentPageNumber, imageLoading, fImageRef, fitToScreen, expanded]);

  useEffect(() => {
    const _canvas = fCanvasRef.current;
    const _img = fImageRef.current;
    if (!_canvas || !_img) {
      return;
    }
    if (fRectRef.current) {
      _canvas.remove(fRectRef.current);
      fRectRef.current = undefined;
    }
    if (offlineHighlightRubberBandData.current?.pageNo === _img.get('pageNumber') && !imageLoading) {
      highlightRubberBand(offlineHighlightRubberBandData.current);
    }
  }, [offlineHighlightRubberBandData, currentPageNumber, imageLoading]);

  useEffect(() => {
    setImageLoading(true);
  }, [currentPageNumber]);

  const calculateRubberbandMetrics = useCallback((_fRect: Rect, scaleX: number, scaleY: number, skipUpdate = false) => {
    const _metrics = {
      startX: Math.round(_fRect.left / scaleX),
      startY: Math.round(_fRect.top / scaleY),
      width: Math.round(_fRect.width / scaleX),
      height: Math.round(_fRect.height / scaleY)
    };
    if (!skipUpdate) setRubberbandMetrics(_metrics);
    return _metrics;
  }, []);

  const calculateMetricWithZoom = (metric: number): number => {
    let newValue: number = 0;
    const _canvas = fCanvasRef.current;
    if (!_canvas) return metric;
    const zoom = _canvas.getZoom() * 100;
    newValue = (metric * zoom) / 100;
    return newValue;
  };

  const calculateRubberbandMetricsAfterScale = useCallback((_fRect: Rect, scaleX: number, scaleY: number) => {
    const _metrics = {
      startX: Math.round(_fRect.left * scaleX),
      startY: Math.round(_fRect.top * scaleY),
      width: Math.round(_fRect.width * scaleX),
      height: Math.round(_fRect.height * scaleY)
    };

    setRubberbandMetrics(_metrics);
    return _metrics;
  }, []);

  const calculateRubberbandDialogPosition = useCallback(
    (_metrics: RubberbandMetrics, isEdit: boolean = false) => {
      if (!canvasRef.current || !fCanvasRef.current || !fImageRef.current) {
        return;
      }
      const { startX, startY, width, height } = _metrics;
      const canvasParent = canvasRef.current.parentNode;
      const canvasParentRect = canvasParent.getBoundingClientRect();
      const zoom = fCanvasRef.current.getZoom();
      const { scaleX, scaleY } = fImageRef.current;
      const hScale = zoom !== 1 ? zoom : scaleX;
      const vScale = zoom !== 1 ? zoom : scaleY;
      const widthDiff = width * hScale - RUBBER_BAND_DIALOG_WIDTH;
      let estimateddTop = canvasParentRect.top + startY * vScale + height * vScale - canvasParent.scrollTop;
      let estimatedLeft =
        canvasParentRect.left + startX * hScale + (width * hScale) / 2 - RUBBER_BAND_DIALOG_WIDTH / 2 - canvasParent.scrollLeft;
      if (estimateddTop + RUBBER_BAND_DIALOG_WIDTH / 2 > canvasParent.getBoundingClientRect().bottom) {
        estimateddTop -= height * vScale + 80;
      }
      if (startX * hScale - canvasParent.scrollLeft < Math.abs(widthDiff / 2)) {
        //estimatedLeft -= widthDiff / 2;
      } else if (
        canvasParentRect.left + startX * hScale - canvasParent.scrollLeft + width * hScale + Math.abs(widthDiff / 2) >
        canvasParentRect.right
      ) {
        //estimatedLeft += widthDiff / 2;
      }
      if (isLabelRubberbandEnabled) {
        estimatedLeft = canvasParentRect.left + startX * hScale + (width * hScale) / 2 - (45 + canvasParent.scrollLeft);
      }
      setRubberbandDialogPosition({
        left: estimatedLeft,
        top: estimateddTop,
        isEdit
      });
    },
    [isLabelRubberbandEnabled]
  );

  // Mouse down event handler
  const onMouseDown = useCallback((options: any) => {
    const _canvas = fCanvasRef.current;
    if (!_canvas) {
      console.error('Canvas not found');
      return;
    }

    isDrawing.current = true;
    // const pointer = _canvas.getPointer(options.e);
    const pointer = _canvas.getScenePoint(options.e);
    startX.current = pointer.x;
    startY.current = pointer.y;

    if (fRectRef.current) {
      _canvas.remove(fRectRef.current);
    }

    // Use the ref to get the latest value of isLabelRubberbandEnabled
    const strokeColor = isLabelRubberbandEnabledRef.current || isAddPatternRubberBandEnabledRef.current ? '#00bebb' : '#f68d2e';
    const fillColor =
      isLabelRubberbandEnabledRef.current || isAddPatternRubberBandEnabledRef.current ? 'rgba(0, 190, 187, 0.5)' : 'rgba(246,141,46,0.5)';

    fRectRef.current = new Rect({
      left: startX.current,
      top: startY.current,
      stroke: strokeColor,
      strokeWidth: 0,
      fill: fillColor,
      width: 0,
      height: 0,
      selectable: false
    });

    _canvas.add(fRectRef.current);
  }, []);

  // Highlight rubberband
  const highlightRubberBand = (data?: RectMetricsForRubberBandType | null) => {
    const _img = fImageRef.current;
    if (!_img || (data?.pageNo && currentPageNumber !== data.pageNo)) {
      offlineHighlightRubberBandData.current = data || null;
    } else {
      const _canvas = fCanvasRef.current;
      if (data) {
        const _image = fImageRef.current;
        const canvasParent = canvasRef.current.parentNode;

        if (!_canvas || !_image || !canvasParent) return;

        isDrawing.current = false;
        const { scaleX, scaleY } = _image;
        const _metrics = calculateRubberbandMetricsAfterScale(data as any, scaleX, scaleY);
        setRubberbandMetrics(_metrics);
        _canvas.renderAll();

        // Use the ref to get the latest value of isLabelRubberbandEnabled

        if (qcRectRef.current) {
          _canvas.remove(qcRectRef.current);
        }

        qcRectRef.current = new Rect({
          left: _metrics.startX,
          top: _metrics.startY,
          stroke: '#00BEBB',
          strokeWidth: 0,
          fill: 'rgba(0, 190, 187, 0.5)',
          width: _metrics.width,
          height: _metrics.height,
          selectable: false
        });

        if (shouldCaptureRectImgContent) {
          setRectImageContent(
            _canvas.toDataURL({
              format: 'png',
              left: calculateMetricWithZoom(_metrics.startX),
              top: calculateMetricWithZoom(_metrics.startY),
              width: calculateMetricWithZoom(_metrics.width),
              height: calculateMetricWithZoom(_metrics.height),
              multiplier: 2
            })
          );
        }

        _canvas.add(qcRectRef.current);

        const zoom = _canvas.getZoom();
        const vt = _canvas.viewportTransform;
        const browserZoom = window.devicePixelRatio || 1;

        const transformedX = _metrics.startX * zoom + (vt?.[4] || 0) * browserZoom;
        const transformedY = _metrics.startY * zoom + (vt?.[5] || 0) * browserZoom;

        canvasParent.scrollTo({
          top: transformedY - 100,
          left: transformedX - 100,
          behavior: 'smooth'
        });

        if (data.showDialog) {
          calculateRubberbandDialogPosition(_metrics);
          setShowRubberbandDialog(true);
        }
        if (enableRightClick) {
          _canvas.on('contextmenu', onContextMenu(_metrics));
        }
      } else {
        if (qcRectRef.current && _canvas) {
          _canvas.remove(qcRectRef.current);
        }
        qcRectRef.current = null;
        setRectImageContent(null);
      }
    }
  };

  // Mouse move event handler
  const onMouseMove = useCallback((options: any) => {
    if (!isDrawing.current || !fCanvasRef.current) return;

    const _canvas = fCanvasRef.current;

    // const pointer = _canvas.getPointer(options.e);
    const pointer = _canvas.getScenePoint(options.e);
    const width = Math.abs(pointer.x - startX.current);
    const height = Math.abs(pointer.y - startY.current);

    if (startX.current > pointer.x) {
      fRectRef.current?.set({
        left: Math.abs(pointer.x)
      });
    }

    if (startY.current > pointer.y) {
      fRectRef.current?.set({
        top: Math.abs(pointer.y)
      });
    }

    fRectRef.current?.set({ width, height });
    _canvas.renderAll();
  }, []);
  const onMouseUp = useCallback(() => {
    const _canvas = fCanvasRef.current;
    const _rect = fRectRef.current;
    const _image = fImageRef.current;

    if (!_canvas || !_rect || !_image) return;

    isDrawing.current = false;
    _rect.set({ selectable: false });
    _rect.setCoords();
    const { scaleX, scaleY } = _image;

    if (Math.abs(_rect.width) >= 10 && Math.abs(_rect.height) >= 10) {
      const _metrics = calculateRubberbandMetrics(_rect, scaleX, scaleY);

      if (shouldCaptureRectImgContent) {
        _canvas.remove(_rect);
        if (qcRectRef.current) {
          _canvas.remove(qcRectRef.current);
        }
        const _metricsInCurrentView = calculateRubberbandMetrics(_rect, 1, 1, true);
        setRectImageContent(
          _canvas.toDataURL({
            format: 'png',
            left: calculateMetricWithZoom(_metricsInCurrentView.startX),
            top: calculateMetricWithZoom(_metricsInCurrentView.startY),
            width: calculateMetricWithZoom(_metricsInCurrentView.width),
            height: calculateMetricWithZoom(_metricsInCurrentView.height),
            multiplier: 2
          })
        );
        _canvas.add(_rect);
        if (qcRectRef.current) {
          _canvas.add(qcRectRef.current);
        }
      }

      if (isLabelRubberbandEnabledRef.current) {
        setLabelRubberbandMetrics(_metrics);
        calculateRubberbandDialogPosition(_metrics); // Update dialog position if needed
        setShowLabelRubberbandDialog(true);
      } else {
        const _metrics = calculateRubberbandMetrics(_rect, scaleX, scaleY);
        calculateRubberbandDialogPosition(_metrics);
        setRubberbandMetrics(_metrics);
        setShowRubberbandDialog(true);
      }
    } else {
      _canvas.remove(_rect);
    }

    _canvas.renderAll();
  }, [calculateRubberbandDialogPosition, calculateRubberbandMetrics, shouldCaptureRectImgContent]);

  const zoomReset = useCallback(() => {
    const _canvas = fCanvasRef.current;
    if (!_canvas) {
      return;
    }
    _canvas.setZoom(1);
    setCurrentZoomLevel(100);
    setIsMaxZoomIn(false);
    setIsMaxZoomOut(false);
  }, []);

  const initCanvas = useCallback(async () => {
    if (fCanvasRef.current) {
      return;
    }

    const canvas = new Canvas(canvasRef.current === null ? undefined : canvasRef.current, {
      hoverCursor: 'default',
      selection: false,
      fireRightClick: true, // <-- enable firing of right click events
      stopContextMenu: true // <--  prevent context menu from showing
    });

    fCanvasRef.current = canvas;
  }, []);

  const removeCurrentImage = () => {
    const canvas = fCanvasRef.current;
    if (!canvas) {
      return;
    }
    if (fImageRef.current) {
      canvas.remove(fImageRef.current);
    }
    if (fRectRef.current) {
      canvas.remove(fRectRef.current);
    }
    if (qcRectRef.current) {
      canvas.remove(qcRectRef.current);
    }
  };

  const loadImage = useCallback(
    async (bytes: string, pageNumber: number, rubberBandHighlightData?: RectMetricsForRubberBandType) => {
      const canvas = fCanvasRef.current;
      if (!canvas) {
        return;
      }
      setImageLoading(true);
      zoomReset();
      if (fImageRef.current) {
        canvas.remove(fImageRef.current);
      }
      const image = new Image();
      image.src = bytes;
      image.onload = () => {
        const img = new FabricImage(image);
        img.set({
          originX: 'left',
          originY: 'top',
          left: 0,
          top: 0,
          selectable: false,
          pageNumber
        });
        canvas.setDimensions({
          width: img.width,
          height: img.height
        });
        canvas.add(img);

        canvas.renderAll();
        fImageRef.current = img;
        if (fitToScreen) onFitToScreenSelect();
        setImageLoading(false);
        if (rubberBandHighlightData) {
          setTimeout(() => highlightRubberBand(rubberBandHighlightData), 0); // setTimeout will execute after all UI render part is done.
        }
      };
    },
    [zoomReset, fitToScreen]
  );

  const onContextMenu = useCallback(
    (rubberbandMetrics?: RubberbandMetrics) => (options: any) => {
      if (rubberbandMetrics) {
        const screenRubberBandX = canvasRef.current.getBoundingClientRect().left + rubberbandMetrics?.startX;
        const screenRubberBandY = canvasRef.current.getBoundingClientRect().top + rubberbandMetrics?.startY;
        const mouseX = options.e.clientX;
        const mouseY = options.e.clientY;

        // To check the mouse is clicked with the rubber band area
        if (
          mouseX >= screenRubberBandX &&
          mouseX <= screenRubberBandX + rubberbandMetrics.width &&
          mouseY >= screenRubberBandY &&
          mouseY <= screenRubberBandY + rubberbandMetrics.height
        ) {
          setShowRubberbandDialog(true);
          calculateRubberbandDialogPosition(rubberbandMetrics, true);
        }
      }
    },
    [calculateRubberbandDialogPosition]
  );

  useEffect(() => {
    return () => {
      if (fCanvasRef.current) {
        const _canvas = fCanvasRef.current;
        _canvas.off('mouse:down', onMouseDown);
        _canvas.off('mouse:move', onMouseMove);
        _canvas.off('mouse:up', onMouseUp);
        _canvas.off('contextmenu', onContextMenu);
        if (!import.meta.env.DEV) {
          _canvas.dispose();
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fix for ordering the definition of onRubberbandSelect and onRubberbandUnSelect before using them
  const onRubberbandSelect = useCallback(() => {
    const canvas = fCanvasRef.current;
    if (!canvas) {
      console.error('Canvas is not initialized');
      return;
    }

    canvas.on('mouse:down', onMouseDown);
    canvas.on('mouse:move', onMouseMove);
    canvas.on('mouse:up', onMouseUp);
    setEnableRubberBand(true);
  }, [onMouseDown, onMouseMove, onMouseUp, setEnableRubberBand]);

  const onRubberbandUnSelect = useCallback(() => {
    const canvas = fCanvasRef.current;
    if (!canvas) {
      return;
    }
    canvas.off('mouse:down', onMouseDown);
    canvas.off('mouse:move', onMouseMove);
    canvas.off('mouse:up', onMouseUp);
    setEnableRubberBand(false);
  }, [onMouseDown, onMouseMove, onMouseUp, setEnableRubberBand]);

  const enableLabelRubberband = useCallback(() => {
    // setIsLabelRubberbandEnabled(true)
    dispatch(setLabelRubberbandEnabled());
  }, []);

  useEffect(() => {
    if (isLabelRubberbandEnabled) {
      onRubberbandSelect();
    } else if (isLabelRubberbandEnabled === null) {
      if (fCanvasRef.current && fRectRef.current) {
        fCanvasRef.current.remove(fRectRef.current);
        fRectRef.current = undefined;
        fCanvasRef.current.renderAll();
      }
    } else {
      onRubberbandUnSelect();
    }
  }, [isLabelRubberbandEnabled, onRubberbandSelect, onRubberbandUnSelect]);

  const disableLabelRubberband = useCallback(() => {
    // setIsLabelRubberbandEnabled(false);
    dispatch(setLabelRubberbandDisabled());

    onRubberbandUnSelect();
    setLabelRubberbandMetrics(null);
  }, [isLabelRubberbandEnabled, onRubberbandUnSelect]);

  const _rotateBy = useCallback(
    (angle: number, isFitToScreen = false) => {
      const parentCanvas = canvasRef.current;
      const canvas = fCanvasRef.current;
      const image = fImageRef.current;

      if (!canvas || !image || !parentCanvas) return;

      const parentBounds = parentCanvas.parentNode.getBoundingClientRect();

      // normalize angle
      const normalizedAngle = ((angle % 360) + 360) % 360;
      image.rotate(normalizedAngle);

      // always rotate around center
      image.set({
        originX: 'center',
        originY: 'center'
      });

      let canvasWidth = 0;
      let canvasHeight = 0;

      if (isFitToScreen) {
        const isPortrait = normalizedAngle === 90 || normalizedAngle === 270;

        // scale only by width → vertical scroll allowed
        const scale = isPortrait ? parentBounds.width / image.height! : parentBounds.width / image.width!;

        image.scale(scale);

        canvasWidth = parentBounds.width;
        canvasHeight = isPortrait ? image.width! * scale : image.height! * scale;

        // reset pan/zoom
        canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
      } else {
        // no fit → natural rotated bounds
        image.scale(1);
        const rad = (normalizedAngle * Math.PI) / 180;
        const cos = Math.abs(Math.cos(rad));
        const sin = Math.abs(Math.sin(rad));

        canvasWidth = image.width! * cos + image.height! * sin;
        canvasHeight = image.width! * sin + image.height! * cos;
      }

      // resize canvas
      canvas.setDimensions({
        width: canvasWidth,
        height: canvasHeight
      });

      // center image every time
      image.set({
        left: canvasWidth / 2,
        top: canvasHeight / 2
      });

      image.setCoords();
      canvas.requestRenderAll();
    },
    [fCanvasRef, fImageRef, canvasRef]
  );

  const onFitToScreenSelect = useCallback(() => {
    const image = fImageRef.current;
    if (!image) return;

    zoomReset();
    setFitToScreen(true);

    _rotateBy(image.angle ?? 0, true);
  }, [zoomReset, _rotateBy]);

  const onFitToScreenUnSelect = useCallback(() => {
    const image = fImageRef.current;
    if (!image) return;

    setFitToScreen(false);
    image.scale(1);

    _rotateBy(image.angle ?? 0, false);
  }, [_rotateBy]);

  const onRotateLeft = useCallback(() => {
    const image = fImageRef.current;
    if (!image) return;

    _rotateBy((image.angle ?? 0) - 90, fitToScreen);
    onRotateResetBack?.();
  }, [_rotateBy, fitToScreen, onRotateResetBack]);

  const onRotateRight = useCallback(() => {
    const image = fImageRef.current;
    if (!image) return;

    _rotateBy((image.angle ?? 0) + 90, fitToScreen);
    onRotateResetBack?.();
  }, [_rotateBy, fitToScreen, onRotateResetBack]);

  const onZoomIn = useCallback(() => {
    const _canvas = fCanvasRef.current;
    const _image = fImageRef.current;
    if (!_canvas || !_image) {
      return;
    }
    const currentZoomLevel = Number(_canvas.getZoom());
    const nextZoomLevel = zoomLevels[zoomLevels.indexOf(currentZoomLevel) + 1];
    if (!nextZoomLevel) {
      return;
    }
    _canvas.setZoom(nextZoomLevel);
    setCurrentZoomLevel(nextZoomLevel * 100);
    setIsMaxZoomIn(zoomLevels.indexOf(nextZoomLevel) >= zoomLevels.length - 1);
    _canvas.setDimensions({
      width: _image.width * nextZoomLevel,
      height: _image.height * nextZoomLevel
    });
    _canvas.renderAll();
  }, [fCanvasRef, fImageRef]);

  const onZoomOut = useCallback(() => {
    const _canvas = fCanvasRef.current;
    const _image = fImageRef.current;
    if (!_canvas || !_image) {
      return;
    }
    const currentZoomLevel = Number(_canvas.getZoom());
    const nextZoomLevel = zoomLevels[zoomLevels.indexOf(currentZoomLevel) - 1];
    if (!nextZoomLevel) {
      return;
    }
    _canvas.setZoom(nextZoomLevel);
    setCurrentZoomLevel(nextZoomLevel * 100);
    setIsMaxZoomOut(zoomLevels.indexOf(nextZoomLevel) <= 0);
    _canvas.setDimensions({
      width: _image.width * nextZoomLevel,
      height: _image.height * nextZoomLevel
    });
    _canvas.renderAll();
  }, []);

  const onDualScreenClose = useCallback(() => {
    setDualScreenEnabled(false);
  }, []);

  const checkDualScreenWindowClosed = useCallback(() => {
    if (!dualScreenWindowRef) {
      return;
    }
    const _window = dualScreenWindowRef.current;
    if (_window && _window.closed) {
      dualScreenWindowRef.current = null;
      clearInterval(dualScreenIntervalRef.current);
      dualScreenIntervalRef.current = undefined;
      onDualScreenClose();
    }
  }, [dualScreenWindowRef, onDualScreenClose]);
  const location = useLocation();
  const onDualScreen = useCallback(() => {
    if (!dualScreenWindowRef) {
      return;
    }
    setDualScreenEnabled(true);

    const _url = `${window.location.origin}/${secondaryScreenUrl}/${fileId}?dualScreen=true`;

    dualScreenWindowRef.current = window.open(
      _url,
      'CMR_Document_Configuration_DualMode',
      `toolbar=yes,resizable=yes,height=${window.screen.height},width=${window.screen.width},scrollbars=yes,status=yes`
    );
    localStorage.setItem('isFromDPC', location?.state?.isFromDPC ? 'true' : 'false');
    localStorage.setItem('currenPageNumber', String(currentPageNumber));
    dualScreenIntervalRef.current = setInterval(checkDualScreenWindowClosed, 1000);
  }, [checkDualScreenWindowClosed, dualScreenWindowRef, fileId, secondaryScreenUrl, currentPageNumber]);

  useEffect(() => {
    dispatch(toggleMenuBar(!dualScreenEnabled));
    const _fCanvas = fCanvasRef.current;
    const _image = fImageRef.current;
    const _canvas = canvasRef.current;
    if (!_fCanvas || !_image || !_canvas) {
      return;
    }
    _image.visible = !dualScreenEnabled;
    _fCanvas.renderAll();
    _canvas.parentNode.style.display = dualScreenEnabled ? 'none' : 'flex';
  }, [dispatch, dualScreenEnabled]);

  const onRubberbandDialogDismiss = useCallback(() => {
    setShowRubberbandDialog(false);
    if (fCanvasRef.current && fRectRef.current) {
      fCanvasRef.current.remove(fRectRef.current);
      fRectRef.current = undefined;
      fCanvasRef.current.renderAll();
    }
  }, []);

  const closeRubberbandDialog = useCallback(() => {
    setShowRubberbandDialog(false);
  }, []);

  const clearRubberBand = () => {
    const _canvas = fCanvasRef.current;
    if (!_canvas) return;
    if (fRectRef.current) {
      _canvas.remove(fRectRef.current);
    }
    if (qcRectRef.current) {
      _canvas.remove(qcRectRef.current);
    }
  };

  return {
    canvasRef: canvasRefCallback,
    onRotateLeft,
    onRotateRight,
    setRubberbandDialogPosition,
    onZoomIn,
    onZoomOut,
    showRubberbandDialog,
    rubberbandMetrics,
    rubberbandDialogPosition,
    onRubberbandDialogDismiss,
    closeRubberbandDialog,
    onRubberbandSelect,
    onRubberbandUnSelect,
    onFitToScreenSelect,
    onFitToScreenUnSelect,
    onDualScreen,
    dualScreenEnabled,
    currentZoomLevel,
    isMaxZoomIn,
    isMaxZoomOut,
    imageLoading,
    initCanvas,
    loadImage,
    isLabelRubberbandEnabled,
    labelRubberbandMetrics,
    enableLabelRubberband,
    disableLabelRubberband,
    showLabelRubberbandDialog,
    setShowLabelRubberbandDialog,
    setShowRubberbandDialog,
    highlightRubberBand,
    rectImageContent,
    removeCurrentImage,
    canvasRefDom: canvasRef,
    fitToScreen,
    enableRubberBand,
    clearRubberBand
  };
};

export default useFabricCanvas;
